import { z } from 'zod';
import { IdSchema, E164Schema, TimestampSchema } from './common.js';

/**
 * The tenant-facing feature set: hunt lists, queues, IVRs, schedules, DIDs.
 *
 * A DESTINATION is a discriminated union rather than a column per target type.
 * A call can be sent to an extension, a hunt list, a queue, an IVR, voicemail
 * or an external number, and modelling that as six nullable foreign keys gives
 * a table that is mostly nulls and a validity rule no constraint can express.
 */
export const DestinationSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('extension'), id: IdSchema }),
  z.object({ type: z.literal('ring_group'), id: IdSchema }),
  z.object({ type: z.literal('queue'), id: IdSchema }),
  z.object({ type: z.literal('ivr'), id: IdSchema }),
  z.object({ type: z.literal('voicemail'), id: IdSchema }),
  z.object({ type: z.literal('external'), number: E164Schema }),
  z.object({ type: z.literal('hangup') }),
]);
export type Destination = z.infer<typeof DestinationSchema>;

// ------------------------------------------------------------- hunt lists
/**
 * What a hunt list can do on its own: everybody at once, one after another in
 * the order listed, or one after another in an order drawn for each call. Round
 * robin and longest idle need to remember who took the last call, which is a
 * queue's job (docs/26 D6, migration 0049).
 */
export const RingStrategySchema = z.enum(['simultaneous', 'sequential', 'random']);

export const RingGroupMemberSchema = z.object({
  id: IdSchema,
  extension_id: IdSchema.nullable(),
  extension_number: z.string().nullable(),
  extension_name: z.string().nullable(),
  external_number: E164Schema.nullable(),
  position: z.number().int(),
  delay_sec: z.number().int(),
});

/**
 * One step of a hunt list (0143, owner 28 September 2026): who rings, how, for how long, and a
 * recording played to the caller before it rings. Steps ring in order; when the last one is not
 * answered the call goes to the list's timeout destination.
 */
export const RingStepStrategySchema = z.enum(['simultaneous', 'sequential', 'random']);
export const RingGroupStepSchema = z.object({
  seq: z.number().int(),
  strategy: RingStepStrategySchema,
  /** Simultaneous: how long the step rings everyone. One at a time: how long each member rings. */
  ring_sec: z.number().int(),
  prompt_media_id: IdSchema.nullable(),
  prompt_name: z.string().nullable(),
  members: z.array(RingGroupMemberSchema).default([]),
});
export type RingGroupStep = z.infer<typeof RingGroupStepSchema>;

export const RingGroupSchema = z.object({
  id: IdSchema,
  tenant_id: IdSchema,
  name: z.string(),
  extension: z.string().nullable(),
  strategy: RingStrategySchema,
  ring_time_sec: z.number().int(),
  skip_busy: z.boolean(),
  timeout_destination: DestinationSchema.nullable(),
  members: z.array(RingGroupMemberSchema).default([]),
  /** The list's steps, in order (0143). `strategy`, `ring_time_sec` and `members` are step 1's. */
  steps: z.array(RingGroupStepSchema).default([]),
  /**
   * The extension an outside member's call is placed as (0144): whose policy, credit and spend
   * limit it counts against. Needed as soon as a step has an outside number.
   */
  outside_extension_id: IdSchema.nullable().optional(),
  created_at: TimestampSchema,
});
export type RingGroup = z.infer<typeof RingGroupSchema>;

export const RingGroupUpsertSchema = z.object({
  name: z.string().min(1).max(120),
  extension: z.string().regex(/^\d{2,6}$/).nullable().optional(),
  strategy: RingStrategySchema.default('simultaneous'),
  ring_time_sec: z.number().int().min(5).max(300).default(20),
  skip_busy: z.boolean().default(true),
  timeout_destination: DestinationSchema.nullable().optional(),
  members: z.array(z.object({
    extension_id: IdSchema.optional(),
    external_number: E164Schema.optional(),
    position: z.number().int().min(0).max(100).default(0),
    delay_sec: z.number().int().min(0).max(300).default(0),
  })).default([]),
  /**
   * The steps (0143). When given they replace strategy, ring_time_sec and members, which are kept
   * for older callers and describe a one-step list.
   */
  steps: z.array(z.object({
    strategy: RingStepStrategySchema.default('simultaneous'),
    ring_sec: z.number().int().min(5).max(300).default(20),
    prompt_media_id: IdSchema.nullable().optional(),
    /** An extension of this account, or an outside number (+E.164, 0144). */
    members: z.array(z.union([
      z.object({ extension_id: IdSchema }).strict(),
      z.object({ external_number: E164Schema }).strict(),
    ])).min(1).max(50),
  })).min(1).max(10).optional(),
  outside_extension_id: IdSchema.nullable().optional(),
}).strict();

// ----------------------------------------------------------------- queues
export const QueueStrategySchema = z.enum([
  'longest_idle', 'round_robin', 'ring_all', 'top_down',
  'agent_with_least_talk_time', 'random',
]);

export const QueueAgentSchema = z.object({
  id: IdSchema,
  extension_id: IdSchema,
  extension_number: z.string(),
  extension_name: z.string(),
  tier_level: z.number().int(),
  tier_position: z.number().int(),
});

export const QueueSchema = z.object({
  id: IdSchema,
  tenant_id: IdSchema,
  name: z.string(),
  extension: z.string().nullable(),
  strategy: QueueStrategySchema,
  announce_position: z.boolean(),
  announce_interval_sec: z.number().int(),
  max_wait_sec: z.number().int(),
  max_waiting_calls: z.number().int(),
  wrapup_sec: z.number().int(),
  /** Whether a call from this queue must be marked before its agent goes ready (0111). */
  require_disposition: z.boolean().optional(),
  /**
   * A caller reaches the agent who last dealt with them, for this many days (0117). Null is off,
   * which is how every queue starts: a support queue wants whoever is free, and only a queue
   * where the agent owns the relationship wants the opposite.
   */
  sticky_agent_days: z.number().int().min(1).max(365).nullable().optional(),
  /** How long that agent is offered the call before it goes into the queue anyway. */
  sticky_ring_sec: z.number().int().min(5).max(60).optional(),
  timeout_destination: DestinationSchema.nullable(),
  /** What callers hear while they wait. Null plays the account's hold music. */
  moh_media_id: IdSchema.nullable().optional(),
  /** A recording played to waiting callers every periodic_interval_sec (0143); null plays none. */
  periodic_media_id: IdSchema.nullable().optional(),
  periodic_interval_sec: z.number().int().optional(),
  agents: z.array(QueueAgentSchema).default([]),
  created_at: TimestampSchema,
});
export type Queue = z.infer<typeof QueueSchema>;

/**
 * A conference room.
 *
 * PINs are digits because they are keyed on a telephone keypad, and short
 * because they are read aloud. They are a shared meeting secret, not a
 * credential -- see migration 0025 for why that distinction is deliberate.
 */
export const ConferenceRoomUpsertSchema = z.object({
  name: z.string().min(1).max(120),
  extension: z.string().regex(/^\d{2,6}$/).nullable().optional(),
  pin: z.string().regex(/^\d{3,10}$/).nullable().optional(),
  moderator_pin: z.string().regex(/^\d{3,10}$/).nullable().optional(),
  wait_for_moderator: z.boolean().default(false),
  end_with_moderator: z.boolean().default(false),
  announce_join: z.boolean().default(true),
  announce_leave: z.boolean().default(false),
  moh_when_alone: z.boolean().default(true),
  record_room: z.boolean().default(false),
  // 0 means the tenant's channel limit is the only ceiling.
  max_members: z.number().int().min(0).max(500).default(0),
  profile: z.enum(['default', 'wideband', 'ultrawideband', 'cdquality']).default('default'),
  greeting_media_id: z.string().uuid().nullable().optional(),
}).strict();

export const QueueUpsertSchema = z.object({
  name: z.string().min(1).max(120),
  extension: z.string().regex(/^\d{2,6}$/).nullable().optional(),
  strategy: QueueStrategySchema.default('longest_idle'),
  /** Tell each waiting caller their place in the queue, every announce_interval_sec (0143). */
  announce_position: z.boolean().default(false),
  announce_interval_sec: z.number().int().min(15).max(600).default(60),
  /** A recording of this account played to waiting callers every periodic_interval_sec (0143). */
  periodic_media_id: z.string().uuid().nullable().optional(),
  periodic_interval_sec: z.number().int().min(15).max(600).default(60),
  max_wait_sec: z.number().int().min(30).max(7200).default(600),
  max_waiting_calls: z.number().int().min(1).max(500).default(50),
  wrapup_sec: z.number().int().min(0).max(600).default(10),
  require_disposition: z.boolean().default(false),
  /**
   * A caller reaches the agent who last dealt with them, for this many days (0117). Null is off,
   * which is how every queue starts: a support queue wants whoever is free, and only a queue
   * where the agent owns the relationship wants the opposite.
   */
  sticky_agent_days: z.number().int().min(1).max(365).nullable().optional().default(null),
  /** How long that agent is offered the call before it goes into the queue anyway. */
  sticky_ring_sec: z.number().int().min(5).max(60).default(15),
  timeout_destination: DestinationSchema.nullable().optional(),
  /** A music-on-hold file of this account. Null or absent plays the account's hold music. */
  moh_media_id: z.string().uuid().nullable().optional(),
  agents: z.array(z.object({
    extension_id: IdSchema,
    tier_level: z.number().int().min(1).max(10).default(1),
    tier_position: z.number().int().min(1).max(100).default(1),
  })).default([]),
}).strict();

// ----------------------------------------------------------- parking lots
/**
 * A parking lot (docs/13 call_park): a park code and a run of slots.
 *
 * Transfer a call to the park code and it waits in the next free slot, which
 * is announced. Dial the slot from any phone in the account to pick it up. A
 * call nobody picks up rings whoever parked it, then goes to the timeout
 * destination; with none, the call ends.
 */
export const PARKING_MAX_SLOTS = 50;

export const ParkingLotSchema = z.object({
  id: IdSchema,
  tenant_id: IdSchema,
  name: z.string(),
  extension: z.string().nullable(),
  slot_start: z.number().int(),
  slot_count: z.number().int(),
  timeout_sec: z.number().int(),
  timeout_destination: DestinationSchema.nullable(),
  announce_slot: z.boolean(),
  moh_media_id: IdSchema.nullable(),
  created_at: TimestampSchema,
});
export type ParkingLot = z.infer<typeof ParkingLotSchema>;

export const ParkingLotUpsertSchema = z.object({
  name: z.string().min(1).max(120),
  extension: z.string().regex(/^\d{2,6}$/).nullable().optional(),
  slot_start: z.number().int().min(10).max(999999),
  slot_count: z.number().int().min(1).max(PARKING_MAX_SLOTS).default(10),
  timeout_sec: z.number().int().min(30).max(3600).default(120),
  timeout_destination: DestinationSchema.nullable().optional(),
  announce_slot: z.boolean().default(true),
  moh_media_id: z.string().uuid().nullable().optional(),
}).strict().refine((v) => v.slot_start + v.slot_count - 1 <= 999999, {
  path: ['slot_count'], message: 'The last slot can be at most six digits.',
});
export type ParkingLotUpsert = z.infer<typeof ParkingLotUpsertSchema>;

// ------------------------------------------------------------------- IVRs
export const IvrOptionSchema = z.object({
  id: IdSchema,
  digit: z.string(),
  label: z.string().nullable(),
  destination: DestinationSchema,
});

export const IvrMenuSchema = z.object({
  id: IdSchema,
  tenant_id: IdSchema,
  name: z.string(),
  extension: z.string().nullable(),
  greeting_media_id: IdSchema.nullable(),
  invalid_media_id: IdSchema.nullable().optional(),
  timeout_sec: z.number().int(),
  /** The gap allowed between digits when direct dialling is on. */
  digit_timeout_sec: z.number().int().optional(),
  max_attempts: z.number().int(),
  /** Rings the caller hears before the menu answers; 0 answers at once. */
  answer_after_rings: z.number().int().optional(),
  direct_dial: z.boolean(),
  timeout_destination: DestinationSchema.nullable(),
  invalid_destination: DestinationSchema.nullable(),
  options: z.array(IvrOptionSchema).default([]),
  created_at: TimestampSchema,
});
export type IvrMenu = z.infer<typeof IvrMenuSchema>;

export const IvrMenuUpsertSchema = z.object({
  name: z.string().min(1).max(120),
  extension: z.string().regex(/^\d{2,6}$/).nullable().optional(),
  greeting_media_id: IdSchema.nullable().optional(),
  /** Played when a caller presses a key with nothing behind it. */
  invalid_media_id: IdSchema.nullable().optional(),
  /** The wait for the first key, once the greeting has played. */
  timeout_sec: z.number().int().min(1).max(60).default(5),
  /** The gap allowed between the digits of an extension number (direct dialling). */
  digit_timeout_sec: z.number().int().min(1).max(10).default(3),
  /** How many times the menu plays before the caller is sent on. */
  max_attempts: z.number().int().min(1).max(10).default(3),
  /** Rings the caller hears before the menu answers, about 3 seconds each. 0 answers at once. */
  answer_after_rings: z.number().int().min(0).max(10).default(0),
  direct_dial: z.boolean().default(false),
  timeout_destination: DestinationSchema.nullable().optional(),
  invalid_destination: DestinationSchema.nullable().optional(),
  options: z.array(z.object({
    digit: z.string().regex(/^[0-9*#]$/),
    label: z.string().max(80).optional(),
    destination: DestinationSchema,
  })).default([]),
}).strict();

// -------------------------------------------------------------- schedules
export const ScheduleRuleSchema = z.object({
  id: IdSchema,
  kind: z.enum(['weekly', 'date_range', 'date']),
  days_of_week: z.array(z.number().int()).nullable(),
  time_from: z.string().nullable(),
  time_to: z.string().nullable(),
  date_from: z.string().nullable(),
  date_to: z.string().nullable(),
  is_open: z.boolean(),
  priority: z.number().int(),
  label: z.string().nullable(),
});

export const ScheduleSchema = z.object({
  id: IdSchema,
  tenant_id: IdSchema,
  name: z.string(),
  timezone: z.string().nullable(),
  rules: z.array(ScheduleRuleSchema).default([]),
  created_at: TimestampSchema,
});
export type Schedule = z.infer<typeof ScheduleSchema>;

export const ScheduleUpsertSchema = z.object({
  name: z.string().min(1).max(120),
  timezone: z.string().max(64).nullable().optional(),
  rules: z.array(z.object({
    kind: z.enum(['weekly', 'date_range', 'date']).default('weekly'),
    days_of_week: z.array(z.number().int().min(0).max(6)).optional(),
    time_from: z.string().regex(/^\d{2}:\d{2}$/).optional(),
    time_to: z.string().regex(/^\d{2}:\d{2}$/).optional(),
    date_from: z.string().optional(),
    date_to: z.string().optional(),
    is_open: z.boolean().default(true),
    /** Higher wins — a holiday must beat the weekly rule beneath it. */
    priority: z.number().int().min(0).max(1000).default(0),
    label: z.string().max(120).optional(),
  })).default([]),
}).strict();

// ------------------------------------------------------------ DIDs & routes
export const NumberSchema = z.object({
  id: IdSchema,
  e164: E164Schema,
  label: z.string().nullable().optional(),
  tenant_id: IdSchema.nullable(),
  tenant_name: z.string().nullable(),
  carrier_id: IdSchema.nullable(),
  carrier_name: z.string().nullable(),
  number_type: z.string(),
  status: z.string(),
  is_emergency_capable: z.boolean(),
  monthly_price: z.string().nullable(),
  currency: z.string().length(3),
  route: z.object({
    id: IdSchema,
    name: z.string(),
    destination: DestinationSchema,
    schedule_id: IdSchema.nullable(),
    closed_destination: DestinationSchema.nullable(),
    record_calls: z.boolean(),
    is_enabled: z.boolean(),
  }).nullable().optional(),
});
export type PhoneNumber = z.infer<typeof NumberSchema>;

// ---------------------------------------------------------- inbound routes
/**
 * When a route's calls take the other branch (docs/26 D2): never, on a shared
 * schedule from Time schedules, or on hours of the route's own.
 */
export const RouteHoursSchema = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('always') }),
  z.object({ mode: z.literal('schedule'), schedule_id: IdSchema }),
  z.object({
    mode: z.literal('own'),
    timezone: z.string().max(64).nullable().optional(),
    rules: ScheduleUpsertSchema.shape.rules,
  }),
]);

/** A named route and the numbers on it (docs/26 D1). */
export const InboundRouteSchema = z.object({
  id: IdSchema,
  name: z.string(),
  // The account's own name for each number (0098), where it has given one.
  numbers: z.array(z.object({ id: IdSchema, e164: E164Schema, label: z.string().nullable().optional() })),
  destination: DestinationSchema,
  closed_destination: DestinationSchema.nullable(),
  hours: z.object({
    mode: z.enum(['always', 'schedule', 'own']),
    schedule_id: IdSchema.nullable(),
    /** The shared schedule's name; null for hours of the route's own. */
    schedule_name: z.string().nullable(),
    timezone: z.string(),
    /** The route's own hours; empty otherwise. */
    rules: z.array(ScheduleRuleSchema),
    /** From the same evaluation the switch makes at call time; null when there is no split. */
    open_now: z.boolean().nullable(),
    explanation: z.string().nullable(),
  }),
  cid_name_prefix: z.string().nullable(),
  is_enabled: z.boolean(),
  created_at: TimestampSchema,
});
export type InboundRoute = z.infer<typeof InboundRouteSchema>;

export const InboundRouteSaveSchema = z.object({
  name: z.string().trim().min(1).max(80),
  /** Numbers on another route are moved here: a number is on one route at a time. */
  number_ids: z.array(IdSchema).max(500).default([]),
  destination: DestinationSchema,
  hours: RouteHoursSchema.default({ mode: 'always' }),
  closed_destination: DestinationSchema.nullable().optional(),
  cid_name_prefix: z.string().max(40).nullable().optional(),
  record_calls: z.boolean().default(false),
  is_enabled: z.boolean().default(true),
}).strict();
export type InboundRouteSave = z.infer<typeof InboundRouteSaveSchema>;
